/* ==========================================================================
   Oraani — custom behaviour.
   Plain custom elements + delegated listeners. No dependencies beyond the
   theme's own Bootstrap bundle (used only for offcanvas triggers in markup).
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- utils */

  /* Every price rendered by Liquid uses `money_without_trailing_zeros`, so
     `Rs. 1,599` on first paint. Shopify.formatMoney follows the shop's money
     format instead, which keeps the decimals — so changing a variant used to
     rewrite the same price as `Rs. 1,599.00`. Format with Shopify (to keep the
     shop's symbol and grouping) then drop a whole-number decimal part, so JS
     and Liquid agree everywhere: PDP, cards, buy-now, cart. */
  const money = (cents) => {
    let out = null;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { out = window.Shopify.formatMoney(cents); } catch (e) { /* fall through */ }
    }

    if (out == null) {
      out = '₹' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    /* Strip `.00` only when it is the final separator group — `\D*$` keeps
       Indian grouping like `1,00,000.00` safe and tolerates a currency suffix
       such as ` INR`. */
    return out.replace(/([.,])00(?=\D*$)/, '');
  };

  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

  /* ------------------------------------------------------------ hero slider */

  class OraaniHero extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.or-hero__viewport');
      this.slides = Array.from(this.querySelectorAll('.or-hero__slide'));
      this.dots = Array.from(this.querySelectorAll('.or-hero__dot'));
      if (!this.viewport || this.slides.length < 2) return;

      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 6000;
      this.autoplay = this.dataset.autoplay === 'true';

      on(this.viewport, 'scroll', () => {
        window.clearTimeout(this._scrollTimer);
        this._scrollTimer = window.setTimeout(() => this.syncFromScroll(), 80);
      }, { passive: true });

      this.dots.forEach((dot) =>
        on(dot, 'click', () => this.goTo(parseInt(dot.dataset.index, 10), true)));

      on(this.querySelector('.or-hero__arrow--prev'), 'click', () => this.goTo(this.index - 1, true));
      on(this.querySelector('.or-hero__arrow--next'), 'click', () => this.goTo(this.index + 1, true));

      // A drag on a linked slide must not fire the link.
      let downX = 0;
      on(this.viewport, 'pointerdown', (e) => { downX = e.clientX; });
      on(this.viewport, 'click', (e) => {
        if (Math.abs(e.clientX - downX) > 8) { e.preventDefault(); e.stopPropagation(); }
      }, true);

      ['pointerdown', 'focusin'].forEach((ev) => on(this, ev, () => this.stop()));
      on(this, 'pointerleave', () => this.start());
      on(document, 'visibilitychange', () => (document.hidden ? this.stop() : this.start()));

      this.start();
    }

    disconnectedCallback() { this.stop(); }

    syncFromScroll() {
      const w = this.viewport.clientWidth || 1;
      const i = clamp(Math.round(this.viewport.scrollLeft / w), 0, this.slides.length - 1);
      if (i !== this.index) { this.index = i; this.paint(); }
    }

    paint() {
      this.dots.forEach((d, i) => d.setAttribute('aria-selected', String(i === this.index)));
    }

    goTo(i, userInitiated) {
      const total = this.slides.length;
      this.index = ((i % total) + total) % total;
      this.viewport.scrollTo({ left: this.index * this.viewport.clientWidth, behavior: 'smooth' });
      this.paint();
      if (userInitiated) { this.stop(); this.start(); }
    }

    start() {
      if (!this.autoplay || this._timer) return;
      this._timer = window.setInterval(() => this.goTo(this.index + 1), this.interval);
    }

    stop() { window.clearInterval(this._timer); this._timer = null; }
  }

  /* ------------------------------------------------------- mobile bottom nav */

  class OraaniMobileNav extends HTMLElement {
    connectedCallback() {
      document.body.classList.add('has-oraani-mobile-nav');

      // The bar is deliberately always visible. Auto-hiding it on scroll-down
      // made it look like the sticky menu was missing entirely.
    }
  }

  /* -------------------------------------------------------- concierge chat */

  class OraaniChat extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[id="or-chat-panel"]');
      this.log = this.querySelector('[data-or-chat-log]');
      this.chips = this.querySelector('[data-or-chat-chips]');
      this.form = this.querySelector('[data-or-chat-form]');
      this.input = this.querySelector('.or-chat__input');
      this.teaser = this.querySelector('[data-or-chat-teaser]');

      const raw = this.querySelector('[data-or-chat-topics]');
      try { this.topics = JSON.parse(raw ? raw.textContent : '[]'); } catch (e) { this.topics = []; }

      this.welcome = (this.querySelector('[data-or-chat-welcome]') || {}).innerHTML || '';
      this.fallback = this.dataset.fallback || '';
      this.whatsapp = this.normaliseWhatsapp(this.dataset.whatsapp);
      this.opened = false;

      // Any element on the page can open the chat (e.g. the founder avatar).
      on(document, 'click', (e) => {
        if (e.target.closest('[data-or-chat-open]')) { e.preventDefault(); this.open(); }
        if (e.target.closest('[data-or-chat-close]')) { e.preventDefault(); this.close(); }
        if (e.target.closest('[data-or-chat-teaser-close]')) { e.preventDefault(); this.hideTeaser(true); }
      });

      on(this.form, 'submit', (e) => {
        e.preventDefault();
        const text = this.input.value.trim();
        if (!text) return;
        this.input.value = '';
        this.say(text, 'out');
        window.setTimeout(() => this.answer(text), 380);
      });

      on(document, 'keydown', (e) => { if (e.key === 'Escape' && this.opened) this.close(); });

      const delay = parseInt(this.dataset.greetingDelay, 10);
      if (this.teaser && !sessionStorage.getItem('or-chat-teaser-seen')) {
        window.setTimeout(() => { if (!this.opened) this.teaser.hidden = false; }, isNaN(delay) ? 6000 : delay);
      }
    }

    normaliseWhatsapp(v) {
      if (!v) return '';
      const s = String(v).trim();
      if (!s) return '';
      if (/^https?:\/\//i.test(s)) return s;
      return 'https://wa.me/' + s.replace(/[^0-9]/g, '');
    }

    hideTeaser(remember) {
      if (!this.teaser) return;
      this.teaser.hidden = true;
      if (remember) sessionStorage.setItem('or-chat-teaser-seen', '1');
    }

    open() {
      this.hideTeaser(true);
      this.panel.hidden = false;
      this.classList.add('is-open');
      this.querySelector('.or-chat__launcher').setAttribute('aria-expanded', 'true');
      this.opened = true;

      if (!this.log.childElementCount) {
        this.say(this.welcome, 'in');
        this.renderChips();
      }
      window.setTimeout(() => this.input && this.input.focus(), 120);
    }

    close() {
      this.panel.hidden = true;
      this.classList.remove('is-open');
      this.querySelector('.or-chat__launcher').setAttribute('aria-expanded', 'false');
      this.opened = false;
    }

    say(html, dir) {
      const row = document.createElement('div');
      row.className = 'or-chat__msg or-chat__msg--' + dir;
      row.innerHTML = html;
      this.log.appendChild(row);
      this.log.scrollTop = this.log.scrollHeight;
      return row;
    }

    renderChips() {
      if (!this.chips) return;
      this.chips.innerHTML = '';
      this.topics.filter((t) => t.quick && t.label).slice(0, 6).forEach((t) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'or-chat__chip';
        b.textContent = t.label;
        on(b, 'click', () => {
          this.say(t.label, 'out');
          window.setTimeout(() => this.reply(t), 320);
        });
        this.chips.appendChild(b);
      });
    }

    // Score each topic by how many of its keywords appear in the question.
    answer(text) {
      const q = text.toLowerCase();
      let best = null;
      let bestScore = 0;

      this.topics.forEach((t) => {
        const words = String(t.keywords || '')
          .split(',').map((w) => w.trim().toLowerCase()).filter(Boolean);
        let score = 0;
        words.forEach((w) => { if (w && q.indexOf(w) !== -1) score += w.length; });
        if (String(t.label || '').toLowerCase() && q.indexOf(String(t.label).toLowerCase()) !== -1) score += 5;
        if (score > bestScore) { bestScore = score; best = t; }
      });

      if (best) return this.reply(best);

      const row = this.say(this.fallback, 'in');
      this.appendHandoff(row);
    }

    reply(topic) {
      const row = this.say(topic.answer || this.fallback, 'in');
      if (topic.link_url && topic.link_label) {
        const a = document.createElement('a');
        a.className = 'or-chat__action';
        a.href = topic.link_url;
        a.textContent = topic.link_label;
        row.appendChild(a);
      }
      // "Find my size" opens the on-page calculator when there is one.
      const calc = document.querySelector('[data-or-sizecalc-open]');
      if (calc && /size|fit|measure|cup|band/i.test(topic.keywords || '')) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'or-chat__action';
        b.textContent = 'Open the size calculator here';
        on(b, 'click', () => { this.close(); calc.click(); });
        row.appendChild(b);
      }
      this.appendHandoff(row);
      this.log.scrollTop = this.log.scrollHeight;
    }

    appendHandoff(row) {
      if (!this.whatsapp) return;
      const a = document.createElement('a');
      a.className = 'or-chat__action or-chat__action--ghost';
      a.href = this.whatsapp;
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = this.dataset.whatsappLabel || 'Chat with our team';
      row.appendChild(a);
    }
  }

  /* ----------------------------------------------------- bra size calculator */

  const CUPS = ['AA', 'A', 'B', 'C', 'D', 'DD', 'E', 'F', 'FF', 'G'];
  const LETTERS = ['S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];

  // Indian sizing (Zivame / Clovia / Amante) uses the +4/+5 rule: the band is
  // the underbust plus 4 if that measurement is even, plus 5 if odd. Checked
  // against published charts — 32B is a 28" underbust with a 34" bust, and
  // 34B is a 30" underbust with a 36" bust.
  function calcSize(underbust, overbust) {
    const u = Math.round(underbust);
    const band = clamp(u % 2 === 0 ? u + 4 : u + 5, 28, 48);

    const diff = Math.round(overbust - band);
    const cupIdx = clamp(diff, 0, CUPS.length - 1);
    const cup = CUPS[cupIdx];

    // Letter sizing tracks the band (32→S, 34→M, 36→L, 38→XL, 40→XXL), with
    // D cups and above taking one extra step in letter-sized garments.
    let letter;
    if (band <= 28) {
      letter = 'XS';
    } else {
      const base = clamp(Math.round((band - 32) / 2), 0, LETTERS.length - 1);
      const bump = cupIdx >= 4 ? 1 : 0;
      letter = LETTERS[clamp(base + bump, 0, LETTERS.length - 1)];
    }

    return { band, cup, cupIdx, cupSize: String(band) + cup, letter };
  }

  const CUP_RE = /^\s*(\d{2})\s*([A-H]{1,3})\s*$/i;
  const LETTER_RE = /^\s*(XS|S|M|L|XL|XXL|2XL|3XL|4XL|XXXL)\s*$/i;

  class OraaniSizeCalc extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-or-sizecalc-panel]');
      this.backdrop = this.querySelector('[data-or-sizecalc-backdrop]');
      this.unit = 'in';

      on(this.querySelector('[data-or-sizecalc-open]'), 'click', () => this.open());
      on(this.querySelector('[data-or-sizecalc-close]'), 'click', () => this.close());
      on(this.backdrop, 'click', () => this.close());
      on(document, 'keydown', (e) => { if (e.key === 'Escape') this.close(); });

      this.querySelectorAll('[data-or-unit]').forEach((btn) =>
        on(btn, 'click', () => this.setUnit(btn.dataset.orUnit)));

      on(this.querySelector('[data-or-sizecalc-run]'), 'click', () => this.run());
      this.querySelectorAll('input[type="number"]').forEach((i) =>
        on(i, 'keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.run(); } }));
    }

    open() { this.panel.hidden = false; this.backdrop.hidden = false; document.body.classList.add('or-noscroll'); }
    close() { this.panel.hidden = true; this.backdrop.hidden = true; document.body.classList.remove('or-noscroll'); }

    setUnit(unit) {
      this.unit = unit;
      this.querySelectorAll('[data-or-unit]').forEach((b) =>
        b.classList.toggle('is-active', b.dataset.orUnit === unit));
      this.querySelectorAll('[data-or-unit-label]').forEach((l) => { l.textContent = unit; });
    }

    // The size option values this product actually sells.
    productSizes() {
      const form = this.closest('product-form') || document.querySelector('product-form');
      const el = form && form.querySelector('[data-variants]');
      let variants = [];
      try { variants = JSON.parse(el ? el.dataset.variants : '[]') || []; } catch (e) { variants = []; }

      const idx = parseInt(this.dataset.sizeOptionIndex, 10);
      const out = [];
      variants.forEach((v) => {
        const value = idx >= 0 && v.options ? v.options[idx] : null;
        if (!value) return;
        if (!out.some((o) => o.value === value)) {
          out.push({ value: value, available: !!v.available });
        } else if (v.available) {
          out.find((o) => o.value === value).available = true;
        }
      });
      return out;
    }

    run() {
      const err = this.querySelector('[data-or-sizecalc-error]');
      const result = this.querySelector('[data-or-sizecalc-result]');
      let under = parseFloat(this.querySelector('[data-or-underbust]').value);
      let over = parseFloat(this.querySelector('[data-or-overbust]').value);

      const show = (msg) => { err.textContent = msg; err.hidden = false; result.hidden = true; };

      if (isNaN(under) || isNaN(over)) return show('Enter both measurements to continue.');
      if (this.unit === 'cm') { under /= 2.54; over /= 2.54; }
      if (under < 22 || under > 60) return show('That underbust looks off — measure snugly, just under the bust.');
      if (over <= under) return show('Your overbust should be larger than your underbust. Check the tape and try again.');
      if (over - under > 14) return show("That's a bigger difference than we stock. Talk to us and we'll help personally.");

      err.hidden = true;
      const size = calcSize(under, over);

      this.querySelector('[data-or-result-cup]').textContent = size.cupSize;
      this.querySelector('[data-or-result-letter]').textContent = size.letter;
      result.hidden = false;

      this.matchToProduct(size);
    }

    matchToProduct(size) {
      const wrap = this.querySelector('[data-or-sizecalc-match]');
      const text = this.querySelector('[data-or-sizecalc-match-text]');
      const apply = this.querySelector('[data-or-sizecalc-apply]');
      const applyLabel = this.querySelector('[data-or-sizecalc-apply-size]');
      const sizes = this.productSizes();

      apply.hidden = true;
      if (!sizes.length) { wrap.hidden = true; return; }
      wrap.hidden = false;

      const usesCups = sizes.some((s) => CUP_RE.test(s.value));
      const usesLetters = sizes.some((s) => LETTER_RE.test(s.value));
      const target = usesCups ? size.cupSize : size.letter;

      let match = sizes.find((s) => s.value.replace(/\s/g, '').toUpperCase() === target.toUpperCase());

      if (!match && usesCups) match = this.nearestCup(sizes, size);
      if (!match && usesLetters) match = this.nearestLetter(sizes, size.letter);

      if (!match) {
        text.textContent = 'This product is sized differently — check its size chart before ordering.';
        return;
      }

      const exact = match.value.replace(/\s/g, '').toUpperCase() === target.toUpperCase();
      if (!match.available) {
        text.textContent = (exact ? target : match.value) + ' is your size on this product, but it is currently out of stock.';
        return;
      }

      text.textContent = exact
        ? 'We stock your size on this product.'
        : 'Exact match unavailable — ' + match.value + ' is the closest fit here.';

      applyLabel.textContent = match.value;
      apply.hidden = false;
      apply.onclick = () => { this.applySize(match.value); this.close(); };
    }

    nearestCup(sizes, size) {
      let best = null;
      let bestDist = Infinity;
      sizes.forEach((s) => {
        const m = CUP_RE.exec(s.value);
        if (!m) return;
        const band = parseInt(m[1], 10);
        const cupIdx = CUPS.indexOf(m[2].toUpperCase());
        if (cupIdx === -1) return;
        // A band step is a bigger fit change than a cup step.
        const dist = Math.abs(band - size.band) * 2 + Math.abs(cupIdx - size.cupIdx);
        if (dist < bestDist || (dist === bestDist && s.available && !(best || {}).available)) {
          bestDist = dist; best = s;
        }
      });
      return best;
    }

    nearestLetter(sizes, letter) {
      const order = ['XS'].concat(LETTERS);
      const want = order.indexOf(letter);
      let best = null;
      let bestDist = Infinity;
      sizes.forEach((s) => {
        const v = s.value.trim().toUpperCase().replace('XXXL', '3XL').replace('2XL', 'XXL');
        const i = order.indexOf(v);
        if (i === -1) return;
        const dist = Math.abs(i - want);
        if (dist < bestDist || (dist === bestDist && s.available && !(best || {}).available)) {
          bestDist = dist; best = s;
        }
      });
      return best;
    }

    // Select the option in the theme's own form so price/stock update with it.
    applySize(value) {
      const form = this.closest('product-form') || document.querySelector('product-form');
      if (!form) return;
      const norm = (s) => String(s).replace(/\s/g, '').toUpperCase();

      const radio = Array.from(form.querySelectorAll('input[type="radio"]'))
        .find((r) => norm(r.value) === norm(value));
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        radio.closest('.product-block-options, product-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const select = Array.from(form.querySelectorAll('select'))
        .find((s) => Array.from(s.options).some((o) => norm(o.value) === norm(value)));
      if (select) {
        select.value = Array.from(select.options).find((o) => norm(o.value) === norm(value)).value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /* ------------------------------------------------------------ price block */

  class OraaniPrice extends HTMLElement {
    connectedCallback() {
      this.months = parseInt(this.dataset.emiMonths, 10) || 6;
      this.emiMin = parseInt(this.dataset.emiMin, 10) || 0;
      on(window, 'kt.product.variant.change', (e) => this.update(e.detail));
      this.update(null);
    }

    update(variant) {
      if (variant) {
        const now = this.querySelector('[data-or-price-now]');
        const was = this.querySelector('[data-or-price-was]');
        const off = this.querySelector('[data-or-price-off]');
        const soldout = this.querySelector('[data-or-price-soldout]');
        const onSale = variant.compare_at_price > variant.price;

        if (now) now.textContent = money(variant.price);
        if (was) {
          was.hidden = !onSale;
          if (onSale) was.textContent = money(variant.compare_at_price);
        }
        if (off) {
          off.hidden = !onSale;
          if (onSale) {
            off.textContent = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100) + '% OFF';
          }
        }
        if (soldout) soldout.hidden = !!variant.available;

        const emi = this.querySelector('[data-or-price-emi]');
        if (emi) {
          const show = variant.price >= this.emiMin;
          emi.hidden = !show;
          if (show) {
            const strong = emi.querySelector('strong');
            if (strong) strong.textContent = money(Math.round(variant.price / this.months)) + '/mo';
          }
        }

        const buy = document.querySelector('[data-or-buy-now]');
        if (buy) {
          buy.dataset.variantId = variant.id;
          buy.disabled = !variant.available;
        }
        return;
      }

      // Initial paint: Liquid already applied the EMI floor using the raw
      // price, so only re-check when the raw price is actually available.
      const emi = this.querySelector('[data-or-price-emi]');
      const raw = parseInt(this.dataset.price, 10);
      if (emi && !isNaN(raw) && this.emiMin > 0) {
        emi.hidden = raw < this.emiMin;
      }
    }
  }

  /* --------------------------------------------------------- buy now button */

  class OraaniBuyNow extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('[data-or-buy-now]');
      on(btn, 'click', () => {
        const form = this.closest('product-form') || document.querySelector('product-form');
        const idField = form && form.querySelector('[name="id"]');
        const id = (idField && idField.value) || btn.dataset.variantId;
        if (!id) return;
        const qtyField = form && form.querySelector('input[name="quantity"]');
        const qty = Math.max(1, parseInt(qtyField ? qtyField.value : '1', 10) || 1);

        btn.classList.add('is-loading');
        // Cart permalink + return_to skips the cart page entirely.
        window.location.href = '/cart/' + id + ':' + qty + '?return_to=/checkout';
      });
    }
  }

  /* ------------------------------------------------------------- copy codes */

  on(document, 'click', (e) => {
    const btn = e.target.closest('[data-or-copy]');
    if (!btn) return;
    const code = btn.dataset.orCopy;
    const done = () => {
      btn.classList.add('is-copied');
      const label = btn.querySelector('span');
      const original = label ? label.textContent : '';
      if (label) label.textContent = 'Copied';
      window.setTimeout(() => {
        btn.classList.remove('is-copied');
        if (label) label.textContent = original;
      }, 1600);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done, done);
    } else {
      const ta = document.createElement('textarea');
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (err) { /* no-op */ }
      document.body.removeChild(ta);
      done();
    }
  });

  /* --------------------------------------------------- search suggestions */

  // Fills the empty state of the search drawer with suggested terms, and
  // rewrites common shorthand so predictive search actually finds things.
  const SEARCH_ALIASES = {
    'tshirt bra': 't-shirt bra',
    'tshirt': 't-shirt',
    'backless': 'stick on',
    'strapless bra': 'strapless',
    'bra for saree': 'padded full coverage',
    'sports bra': 'activewear',
    'nighty': 'nightwear',
    'panty': 'panties',
    'plus size': 'fuller bust'
  };

  function initSearchExtras() {
    const drawer = document.getElementById('offcanvas-search');
    if (!drawer) return;
    const input = drawer.querySelector('input[name="q"]');
    const suggestions = drawer.querySelector('[data-or-search-suggestions]');

    if (suggestions) {
      suggestions.querySelectorAll('[data-or-term]').forEach((btn) => {
        on(btn, 'click', () => {
          input.value = btn.dataset.orTerm;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.focus();
        });
      });
    }

    if (input) {
      on(input, 'input', () => {
        const empty = !input.value.trim();
        if (suggestions) suggestions.hidden = !empty;
      });

      // Expand shorthand on submit so the results page isn't empty.
      const form = input.closest('form');
      on(form, 'submit', () => {
        const q = input.value.trim().toLowerCase();
        if (SEARCH_ALIASES[q]) input.value = SEARCH_ALIASES[q];
      });
    }
  }

  /* --------------------------------------------- collection sticky toolbar */

  function initCollectionToolbar() {
    const bar = document.querySelector('[data-or-toolbar]');
    if (!bar) return;
    const sentinel = document.createElement('div');
    bar.parentNode.insertBefore(sentinel, bar);
    new IntersectionObserver(
      ([entry]) => bar.classList.toggle('is-stuck', !entry.isIntersecting),
      { rootMargin: '-1px 0px 0px 0px', threshold: [1] }
    ).observe(sentinel);
  }

  /* ---------------------------------------------------------------- header */

  class OraaniHeader extends HTMLElement {
    connectedCallback() {
      this.rotateStrip();
      if (this.dataset.sticky === 'true') this.makeSticky();
    }

    rotateStrip() {
      const items = Array.from(this.querySelectorAll('.or-hdr__strip-item'));
      if (items.length < 2) return;
      let i = 0;
      this._strip = window.setInterval(() => {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, 4200);
    }

    /* Three behaviours, set in the theme editor:
         always    — pinned the whole time
         scroll_up — pinned, but slides away going down and returns going up
         off       — sits at the top of the page and scrolls out of view
       What gets pinned is configurable too: the whole header including the
       announcement strip, or just the logo/nav bar.

       Uses `position: fixed` with a spacer rather than `position: sticky`. A
       sticky element can only travel inside its own containing block, and both
       #header-group and <oraani-header> are exactly the header's height — so
       sticky had nowhere to travel and the header simply scrolled away, only
       reappearing when you returned to the very top. */
    makeSticky() {
      const bar = this.querySelector('.or-hdr__bar');
      if (!bar) return;

      const mode = this.dataset.stickyMode || 'always';
      if (mode === 'off') return;

      const withStrip = this.dataset.stickyIncludesStrip !== 'false';
      const pinned = withStrip ? this : bar;

      const spacer = document.createElement('div');
      spacer.className = 'or-hdr__spacer';
      pinned.parentNode.insertBefore(spacer, pinned);
      pinned.classList.add('is-fixed');
      this.classList.toggle('or-hdr--pin-all', withStrip);

      const sizeSpacer = () => {
        spacer.style.height = pinned.offsetHeight + 'px';
      };
      sizeSpacer();
      if ('ResizeObserver' in window) new ResizeObserver(sizeSpacer).observe(pinned);
      window.addEventListener('resize', sizeSpacer);

      const setStuck = () => pinned.classList.toggle('is-stuck', window.scrollY > 2);

      if (mode !== 'scroll_up') {
        window.addEventListener('scroll', setStuck, { passive: true });
        setStuck();
        return;
      }

      const threshold = parseInt(this.dataset.stickyThreshold, 10) || 0;
      let lastY = window.scrollY;
      let ticking = false;

      const update = () => {
        ticking = false;
        const y = window.scrollY;
        const delta = y - lastY;
        setStuck();

        /* Ignore jitter and rubber-band scrolling past the ends of the page. */
        if (Math.abs(delta) < 4 || y < 0) { lastY = y; return; }

        /* Never hide near the top, and never while a drawer is open. */
        const drawerOpen = document.querySelector('.offcanvas.show, .offcanvas.showing');
        if (y <= threshold || drawerOpen) pinned.classList.remove('is-hidden');
        else pinned.classList.toggle('is-hidden', delta > 0);

        lastY = y;
      };

      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      }, { passive: true });

      /* Focusing something inside a hidden header would scroll to nowhere. */
      this.addEventListener('focusin', () => pinned.classList.remove('is-hidden'));
      update();
    }

    disconnectedCallback() { window.clearInterval(this._strip); }
  }

  // Keep the header cart badge in step with the theme's own cart updates.
  const syncCartBadge = () => {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => {
        if (!c) return;
        document.querySelectorAll('[data-or-cart-count]').forEach((el) => {
          el.textContent = c.item_count;
          el.hidden = c.item_count === 0;
        });

        /* The mobile nav and the vendor navbar use the theme's own badge
           markup, which keys off `data-count` rather than our attribute —
           update both so no entry point shows a stale number. */
        document.querySelectorAll('.cart-count-badge').forEach((el) => {
          el.textContent = c.item_count;
          el.setAttribute('data-count', c.item_count);
        });

        /* Shiprocket renders its own header count inside an open shadow root and
           derives it from its internal item list, which goes stale when the
           theme changes the cart — the drawer shows an item while the title
           still says (0). Correct it from the real cart. */
        try {
          const host = document.querySelector('[data-app="shiprocket-smart-cart"]');
          const sr = host && host.shadowRoot;
          const qty = sr && sr.getElementById('sr-cart-header-quantity');
          if (qty) qty.innerText = '(' + c.item_count + ')';
        } catch (_) { /* closed shadow root or app not present */ }

        document.dispatchEvent(new CustomEvent('or:cart:updated', { detail: c }));
      })
      .catch(() => {});
  };
  ['kt.cart.change', 'kt.cart.add', 'kt.cart.update'].forEach((ev) =>
    window.addEventListener(ev, syncCartBadge));

  /* --------------------------------------------------------------- reviews */

  /* The store runs AG Product Reviews (Avada Air Reviews). Its reviews are NOT
     read out of the widget's DOM: the app block prints the whole payload into
     the page as `window.AIR_REVIEWS` (this product's reviews plus a per-star
     summary), and exposes a public read endpoint as a second route. Scraping
     the rendered widget was the earlier approach and it was fragile — the
     widget renders asynchronously, and a folded container may never render at
     all, which is why nothing appeared on products that do have reviews.

     Judge.me is kept as a DOM adapter so a change of app is a small edit here
     rather than a rewrite. */

  const AIR_API = 'https://app.airreviews.io/review/loadReviews';

  const REVIEW_SOURCES = [
    {
      name: 'air',
      /* The app block wrapper, so its own heading and padding fold with it. */
      shell: '.shopify-app-block:has(.AirReviews-Embed-BlockWrapper), .shopify-app-block:has(.AirReviews-Embed-BlockAllWrapper)',
      root: '#AirReviews-BlockWrapper, .AirReviews-Embed-BlockWrapper, #AirReviews-BlockAllWrapper, .AirReviews-Embed-BlockAllWrapper',
      write: '.AirReviews-Blocks__SubmitReview, .AirReviews-NoReview__SubmitReview, .AirReviews-BlockWrapperSubmit',
    },
    {
      name: 'judgeme',
      shell: '.shopify-app-block:has(#judgeme_product_reviews)',
      root: '#judgeme_product_reviews, .jdgm-review-widget, .jdgm-rev-widg',
      item: '.jdgm-rev',
      author: '.jdgm-rev__author',
      body: '.jdgm-rev__body',
      rating: '.jdgm-rev__rating[data-score]',
      pic: '.jdgm-rev__pic-img, .jdgm-rev__pics img',
      empty: '.jdgm-rev-widg--empty',
      summaryRate: '.jdgm-rev-widg__average-text',
      summaryCount: '.jdgm-rev-widg__summary-text',
      write: '.jdgm-write-rev-link',
    },
  ];

  const q = (sel, root) => {
    if (!sel) return null;
    try {
      return (root || document).querySelector(sel);
    } catch (_) {
      return null;   /* an engine without :has() support */
    }
  };

  const reviewSource = () => {
    for (let i = 0; i < REVIEW_SOURCES.length; i += 1) {
      const src = REVIEW_SOURCES[i];
      const root = q(src.root);
      if (root) return Object.assign({}, src, { rootEl: root, shellEl: q(src.shell) || root });
    }
    return null;
  };

  /* Every element the app renders into. AG builds `#AirReviews-BlockWrapper` as
     a sibling of its embed placeholder, so folding the placeholder alone leaves
     the actual widget on screen — which is exactly what was still showing. */
  const REVIEW_APP_PARTS = [
    /* Product page: the review-list block. */
    '.shopify-app-block:has(.AirReviews-Embed-BlockWrapper)',
    '.shopify-app-block:has(#AirReviews-BlockWrapper)',
    '.AirReviews-Embed-BlockWrapper',
    '#AirReviews-BlockWrapper',
    /* Home page: the all-reviews block, which this section now mirrors too. */
    '.shopify-app-block:has(.AirReviews-Embed-BlockAllWrapper)',
    '.shopify-app-block:has(#AirReviews-BlockAllWrapper)',
    '.AirReviews-Embed-BlockAllWrapper',
    '#AirReviews-BlockAllWrapper',
    '.shopify-app-block:has(#judgeme_product_reviews)',
    '#judgeme_product_reviews',
  ];

  const reviewAppParts = () => {
    const found = [];
    REVIEW_APP_PARTS.forEach((sel) => {
      try {
        document.querySelectorAll(sel).forEach((el) => {
          if (found.indexOf(el) === -1) found.push(el);
        });
      } catch (_) { /* an engine without :has() support */ }
    });
    return found;
  };

  /* Folded, never removed: the app owns the review form. */
  const foldReviewApp = () => {
    reviewAppParts().forEach((el) => {
      el.classList.remove('or-app-reviews-open');
      el.classList.add('or-app-reviews-folded');
    });
  };

  const revealReviewApp = () => {
    const parts = reviewAppParts();
    parts.forEach((el) => {
      el.classList.remove('or-app-reviews-folded');
      /* Beats the pre-JS CSS fold as well as the class one. */
      el.classList.add('or-app-reviews-open');
    });
    return parts[0] || null;
  };

  const airGlobal = () => window.AIR_REVIEWS || null;

  /* The per-star breakdown the app prints for this product. Counts are the
     published ones — what a shopper would actually see in the widget. */
  const airSummary = () => {
    const air = airGlobal();
    const s = air && air.productReview && air.productReview.reviewSummary;
    if (!s) return null;

    const buckets = [['five_star', 5], ['four_star', 4], ['three_star', 3], ['two_star', 2], ['one_star', 1]];
    let count = 0;
    let total = 0;
    buckets.forEach(([key, stars]) => {
      const b = s[key] || {};
      const n = Math.max(Number(b.published) || 0, Number(b.approved) || 0);
      count += n;
      total += n * stars;
    });
    return { count, avg: count ? total / count : 0 };
  };

  const airProductId = (host) =>
    (host && host.dataset.orReviewsProduct) ||
    (airGlobal() && airGlobal().product && airGlobal().product.id) ||
    '';

  /* The payload in the page first; the public endpoint only if it is empty.
     Note the summary counters are NOT a gate — AG can report a star breakdown
     of zero for reviews its own API returns as approved, which is why the
     widget shows "0 reviews" on products that plainly have some. */
  const airReviews = (host, opts) => {
    const air = airGlobal();
    const shopWide = opts && opts.scope === 'shop';
    const want = (opts && opts.limit) || 20;

    /* On a product page the app already prints that product's reviews into the
       page, so there is nothing to fetch. Store-wide always goes to the API —
       the inline payload is per product (and `false` off the product page). */
    if (!shopWide) {
      const inline = air && air.productReview && air.productReview.reviews;
      if (Array.isArray(inline) && inline.length) {
        return Promise.resolve({ reviews: inline, hasNext: false });
      }
    }

    const shopId = air && air.settings && air.settings.shopId;
    const productId = shopWide ? '' : airProductId(host);
    if (!shopId || (!shopWide && !productId)) return Promise.resolve({ reviews: [], hasNext: false });

    const url = AIR_API +
      '?shopId=' + encodeURIComponent(shopId) +
      (productId ? '&productId=' + encodeURIComponent(productId) : '') +
      '&sort=newest&minimumRating=all-reviews' +
      '&limitPerPage=' + Math.min(Math.max(want, 10), 50);

    return fetch(url, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => ({
        reviews: (json && json.data && json.data.reviews) || [],
        hasNext: !!(json && json.data && json.data.hasNext),
      }))
      .catch(() => ({ reviews: [], hasNext: false }));
  };

  const publishedReview = (r) =>
    !r.status || r.status === 'approved' || r.status === 'published';

  /* One resolution of "what does this product actually score", shared by the
     reviews section and the rating trust badge, so the two can never disagree
     and the data is fetched once. */
  let reviewSummaryPromise = null;
  const reviewSummary = (host, opts) => {
    if (reviewSummaryPromise) return reviewSummaryPromise;

    const shopWide = opts && opts.scope === 'shop';

    reviewSummaryPromise = airReviews(host || null, opts).then((res) => {
      const reviews = (res.reviews || []).filter(publishedReview);
      /* The per-star summary the app prints is for the current product only. */
      const summary = shopWide ? null : airSummary();
      const rated = reviews.filter((r) => Number(r.rate) > 0);

      let avg = summary && summary.avg;
      let count = summary && summary.count;
      if (!avg && rated.length) {
        avg = rated.reduce((sum, r) => sum + Number(r.rate), 0) / rated.length;
      }
      if (!count) count = reviews.length;

      /* Store-wide reads one page, so the total is a floor, not the figure. */
      return { reviews, avg: avg || 0, count: count || 0, more: !!res.hasNext && shopWide };
    });
    return reviewSummaryPromise;
  };

  /* Badges that quote the score stay hidden until there is a real one. */
  const applyRatingBadges = (avg, count) => {
    document.querySelectorAll('[data-or-rating-badge]').forEach((el) => {
      if (!(avg > 0 && count > 0)) return;
      const tpl = el.dataset.orRatingTemplate || '';
      const slot = el.querySelector('[data-or-rating-text]');
      if (slot) {
        slot.textContent = tpl
          .replace('[rating]', avg.toFixed(1))
          .replace('[reviews]', count)
          .replace('[scale]', '5');
      }
      el.hidden = false;
    });
  };

  const initRatingBadges = () => {
    if (!document.querySelector('[data-or-rating-badge]')) return;
    reviewSummary().then(({ avg, count }) => applyRatingBadges(avg, count));
  };

  const initOraaniReviews = () => {
    const host = document.querySelector('[data-or-reviews]');
    if (!host) return;

    const rail = host.querySelector('[data-or-reviews-rail]');
    const empty = host.querySelector('[data-or-reviews-empty]');
    const cta = host.querySelector('[data-or-reviews-cta]');
    const writeBtn = host.querySelector('[data-or-reviews-writebtn]');
    const averageEl = host.querySelector('[data-or-reviews-average]');
    const scoreEl = host.querySelector('[data-or-reviews-score]');
    const starsEl = host.querySelector('[data-or-reviews-stars]');
    const countEl = host.querySelector('[data-or-reviews-countlabel]');
    const max = parseInt(host.dataset.orReviewsMax || '8', 10);
    const verifiedLabel = host.dataset.orReviewsVerified || '';

    /* Everything the editor can switch off. Absent attribute = on, so the
       section keeps working if a setting is ever removed. */
    const flag = (name) => host.dataset[name] !== 'false';
    const show = {
      stars: flag('orShowStars'),
      avatar: flag('orShowAvatar'),
      name: flag('orShowName'),
      verified: flag('orShowVerified'),
      date: host.dataset.orShowDate === 'true',
      photo: flag('orShowPhoto'),
    };
    show.product = flag('orShowProduct');
    const minRating = parseInt(host.dataset.orMinRating || '0', 10) || 0;
    const photosOnly = host.dataset.orPhotosOnly === 'true';
    const sortBy = host.dataset.orSort || 'newest';
    const scope = host.dataset.orReviewsScope || 'product';
    const countTemplate = host.dataset.orCountTemplate || '';
    const hideWhenEmpty = scope === 'shop' && host.dataset.orEmptyShop !== 'invite';

    /* Out of sight from the start — this section is the one showing them. The
       app builds its widget as a sibling of the placeholder some time after
       load, so keep folding as the DOM changes rather than only once. */
    foldReviewApp();
    const folder = new MutationObserver(() => foldReviewApp());
    folder.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => folder.disconnect(), 15000);

    const escapeHtml = (str) =>
      String(str).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]));

    const firstNumber = (str) => {
      const m = String(str || '').match(/\d+(\.\d+)?/);
      return m ? parseFloat(m[0]) : 0;
    };

    /* Same two-layer construction as the `oraani-stars` snippet, so a card built
       here and one rendered by Liquid are identical — including half stars. */
    const STAR_POINTS = '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2';

    const starRow = (px) => {
      let out = '';
      for (let i = 0; i < 5; i += 1) {
        out += '<svg viewBox="0 0 24 24" width="' + px + '" height="' + px + '" aria-hidden="true" focusable="false">' +
          '<polygon points="' + STAR_POINTS + '"/></svg>';
      }
      return out;
    };

    const stars = (score, px) => {
      const size = px || 15;
      const pct = Math.max(0, Math.min(5, Number(score) || 0)) * 20;
      const row = starRow(size);
      return (
        '<span class="or-stars' + (pct <= 0 ? ' or-stars--empty' : '') + '"' +
        ' style="--or-stars-fill: ' + pct + '%"' +
        ' role="img" aria-label="' + score + ' out of 5 stars">' +
          '<span class="or-stars__row or-stars__base">' + row + '</span>' +
          '<span class="or-stars__row or-stars__fill">' + row + '</span>' +
        '</span>'
      );
    };

    const dateLabel = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      try {
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (_) {
        return d.toDateString();
      }
    };

    const productLink = (prod) => {
      if (!show.product || !prod || !prod.title) return '';
      const href = prod.productLink || '#';
      const img = prod.image
        ? '<img src="' + prod.image + '" alt="" width="44" height="55" loading="lazy">'
        : '';
      return '<a class="or-reviews__product" href="' + href + '">' + img +
        '<span>' + escapeHtml(prod.title.length > 46 ? prod.title.slice(0, 46) + '…' : prod.title) + '</span></a>';
    };

    const cardHtml = ({ score, author, body, pic, verified, date, product }) => {
      if (!body) return '';
      const name = author || 'Verified buyer';
      const initial = name.trim().charAt(0).toUpperCase();

      const badge = show.verified && verified && verifiedLabel
        ? '<span class="or-reviews__verified">' + escapeHtml(verifiedLabel) + '</span>'
        : '';
      const when = show.date && date
        ? '<span class="or-reviews__date">' + escapeHtml(dateLabel(date)) + '</span>'
        : '';
      const who = (show.name ? '<strong class="or-reviews__name">' + escapeHtml(name) + '</strong>' : '') + badge + when;

      return (
        '<li class="or-reviews__item">' +
          '<figure class="or-reviews__card">' +
            (show.stars && score ? stars(score) : '') +
            '<blockquote class="or-reviews__quote">' + escapeHtml(body) + '</blockquote>' +
            (show.photo && pic ? '<img class="or-reviews__pic" src="' + pic + '" alt="" loading="lazy">' : '') +
            productLink(product) +
            (who || (show.avatar && show.name)
              ? '<figcaption class="or-reviews__by">' +
                  (show.avatar ? '<span class="or-reviews__avatar"><span class="or-initial" aria-hidden="true">' + escapeHtml(initial) + '</span></span>' : '') +
                  '<span class="or-reviews__who">' + who + '</span>' +
                '</figcaption>'
              : '') +
          '</figure>' +
        '</li>'
      );
    };

    /* A review image is a URL or an object; take whichever shape turns up. */
    const imageOf = (images) => {
      if (!Array.isArray(images) || !images.length) return '';
      const first = images[0];
      if (typeof first === 'string') return first;
      return (first && (first.url || first.src || first.original || first.thumbnail || first.link)) || '';
    };

    const paint = (cards, avg, count, more) => {
      if (rail) { rail.innerHTML = cards.join(''); rail.hidden = false; }
      if (empty) empty.hidden = true;
      if (cta) cta.hidden = false;
      if (writeBtn) writeBtn.hidden = !reviewSource();
      host.classList.remove('is-loading', 'is-empty');
      host.classList.add('is-ready');

      if (averageEl && avg > 0) {
        averageEl.hidden = false;
        if (scoreEl) scoreEl.textContent = avg.toFixed(1) + '/5';
        const row = starsEl && starsEl.querySelector('.or-stars');
        if (row) {
          row.style.setProperty('--or-stars-fill', (avg * 20) + '%');
          row.classList.toggle('or-stars--empty', avg <= 0);
        }
        if (countEl && count > 0) {
          const shown = count + (more ? '+' : '');
          countEl.textContent = countTemplate
            ? countTemplate.replace('[reviews]', shown)
            : countEl.textContent.replace(/\d+/, shown);
        }
      }

      applyRatingBadges(avg, count);
    };

    const showEmpty = () => {
      host.classList.remove('is-loading');
      host.classList.add('is-empty');
      /* Store-wide with nothing to show: there is no product to review here, so
         an invitation would be meaningless — drop the section instead. */
      if (hideWhenEmpty) { host.hidden = true; return; }
      if (rail) { rail.hidden = true; rail.innerHTML = ''; }
      if (empty) empty.hidden = false;
      if (averageEl) averageEl.hidden = true;
      if (cta) cta.hidden = true;
      if (writeBtn) writeBtn.hidden = true;
    };

    /* Reviews exist but this section could not build a single card — hand the
       page back to the app rather than claiming there are none. */
    const deferToApp = () => {
      revealReviewApp();
      if (rail) rail.hidden = true;
      if (empty) empty.hidden = true;
      host.classList.remove('is-loading');
      host.classList.add('is-deferred');
    };

    /* ---- AG Product Reviews: straight from the app's own data ---- */
    const runAir = () => {
      reviewSummary(host, { scope: scope, limit: max * 2 })
        .then(({ reviews: all, avg: summaryAvg, count: summaryCount, more }) => {
        /* Editor filters and ordering. The average and count still describe
           every review, not just the ones that survive these filters. */
        let usable = all.filter((r) => (Number(r.rate) || 0) >= minRating);
        if (photosOnly) usable = usable.filter((r) => imageOf(r.images));
        if (sortBy === 'highest') {
          usable = usable.slice().sort((a, b) => (Number(b.rate) || 0) - (Number(a.rate) || 0));
        } else if (sortBy === 'lowest') {
          usable = usable.slice().sort((a, b) => (Number(a.rate) || 0) - (Number(b.rate) || 0));
        }

        const cards = usable.slice(0, max).map((r) => cardHtml({
          score: Number(r.rate) || 0,
          author: [r.firstName || r.first_name, r.lastName || r.last_name].filter(Boolean).join(' ').trim(),
          body: (r.content || '').trim(),
          pic: imageOf(r.images),
          verified: !!(r.verified || r.verifyBuyer),
          date: r.createdAt,
          product: r.product || (r.products && r.products[0]),
        })).filter(Boolean);

        if (cards.length) {
          paint(cards, summaryAvg, summaryCount, more);
          return;
        }

        if (summaryCount > 0) {
          deferToApp();
          return;
        }
        showEmpty();
      });
    };

    /* ---- Judge.me and anything else that only exists in the DOM ---- */
    const runDom = (src) => {
      const scoreOf = (item) => {
        const el = item.querySelector(src.rating);
        if (!el) return 0;
        const attr = el.getAttribute('data-score');
        if (attr) return parseFloat(attr) || 0;
        return firstNumber(el.getAttribute('aria-label'));
      };

      const text = (node, sel) => {
        const el = sel ? node.querySelector(sel) : node;
        return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
      };

      const render = () => {
        const items = src.rootEl.querySelectorAll(src.item);
        if (!items.length) {
          if (q(src.empty, src.rootEl)) { showEmpty(); return true; }
          return false;
        }

        const cards = Array.prototype.slice.call(items, 0, max).map((item) => {
          const pic = item.querySelector(src.pic);
          return cardHtml({
            score: scoreOf(item),
            author: text(item, src.author),
            body: text(item, src.body),
            pic: pic ? pic.getAttribute('src') : '',
            verified: item.getAttribute('data-verified-buyer') === 'true',
            date: '',
          });
        }).filter(Boolean);

        if (!cards.length) { deferToApp(); return true; }

        const avg = firstNumber(text(q(src.summaryRate, src.rootEl)));
        const count = firstNumber(text(q(src.summaryCount, src.rootEl))) || items.length;
        paint(cards, avg, count);
        return true;
      };

      if (render()) return;

      const observer = new MutationObserver(() => {
        foldReviewApp();
        if (render()) observer.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });

      window.setTimeout(() => {
        observer.disconnect();
        if (!host.classList.contains('is-ready') && !host.classList.contains('is-deferred')) {
          showEmpty();
        }
      }, 8000);
    };

    /* The app block's inline payload runs during parse, so it is normally here
       already; poll briefly in case the block is placed after this script. */
    let tries = 0;
    const start = () => {
      /* AG prints its payload inline, so this is normally true immediately —
       * and it is true on the home page too, where there is no review widget
       * block at all. */
      if (airGlobal()) { runAir(); return; }

      const src = reviewSource();
      if (src && src.name !== 'air' && src.item) { runDom(src); return; }

      tries += 1;
      if (tries > 20) { showEmpty(); return; }
      window.setTimeout(start, 250);
    };
    start();
  };

  /* "Write a review" hands over to the app's own form. */
  const initReviewWrite = () => {
    on(document, 'click', (e) => {
      const btn = e.target.closest('[data-or-review-write]');
      if (!btn) return;
      e.preventDefault();

      const shell = revealReviewApp();
      const src = reviewSource();
      const link = src ? q(src.write, src.rootEl) || q(src.write) : null;

      if (link) {
        link.click();
        window.setTimeout(() => {
          const form = document.querySelector('.AirReviews-Modal, .AirReviews-Widget__Modal, .jdgm-form-wrapper, .jdgm-form');
          (form || link).scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 250);
        return;
      }

      if (shell) shell.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  /* "Read all reviews" with no link set brings the app's full widget back. */
  const initReviewsShowAll = () => {
    on(document, 'click', (e) => {
      const trigger = e.target.closest('[data-or-reviews-showall]');
      if (!trigger) return;
      e.preventDefault();
      const shell = revealReviewApp();
      if (shell) shell.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };


  /* -------------------------------------------------------- stock indicator */

  /* The theme dims the stock line on a variant change and only restores it when
     its re-fetch of the whole product page comes back, so a slow or failed
     fetch left the previous variant's count on screen. Every variant's line is
     published in `data-stock` by the snippet — apply it straight away. */
  const initStockIndicator = () => {
    window.addEventListener('kt.product.variant.change', (e) => {
      const variant = e.detail;
      if (!variant) return;

      document.querySelectorAll('product-stock-indicator[data-stock]').forEach((el) => {
        let map;
        try {
          map = JSON.parse(el.dataset.stock);
        } catch (_) {
          return;
        }

        const entry = map[variant.id];
        if (!entry) return;

        const inner = el.querySelector('.product-block-stock-indicator-inner');
        const textEl = el.querySelector('[data-stock-text]');
        const bar = el.querySelector('.progress-bar');

        if (textEl) textEl.innerHTML = '<p>' + entry.text + '</p>';

        if (bar) {
          [el.dataset.hasStockColor, el.dataset.lowStockColor].forEach((c) => {
            if (c) bar.classList.remove(c);
          });
          if (entry.bar) bar.classList.add(entry.bar);
          bar.dataset.width = entry.pct + '%';
          bar.style.width = entry.pct + '%';
        }

        /* Undo the theme's dimming — the line is current again. */
        if (inner) inner.style.opacity = '1';
      });
    });
  };

  /* ------------------------------------------------------------------ cart */

  /* Where to send the shopper once something is in the cart. The header trigger
     already carries the Cart behaviour setting, so read it from there rather
     than duplicating the setting in JS. */
  const openCartAfterAdd = () => {
    const trigger = document.querySelector('[data-or-cart-open]');
    if (trigger) {
      /* 'app' goes through initCartHandover (event or launcher click); if the
         app is not there the click falls through to the href, which is /cart. */
      trigger.click();
      return;
    }
    window.location.href = '/cart';
  };

  const showAtcError = (form, message) => {
    const prev = form.querySelector('.or-atc-error');
    if (prev) prev.remove();
    const p = document.createElement('p');
    p.className = 'or-atc-error';
    p.setAttribute('role', 'alert');
    p.textContent = message;
    form.appendChild(p);
    window.setTimeout(() => p.remove(), 6000);
  };

  /* The theme's ProductForm.onSubmit finishes with
       document.querySelector('cart-container').add(formData)
     and the drawer holding <cart-container> is only rendered when Cart
     behaviour is "Theme cart drawer". With a third-party cart app — or the cart
     page — that element does not exist, so every Add to cart threw
     "Cannot read properties of null (reading 'add')" after preventDefault():
     the page did nothing and the item never reached the cart.

     Take the submit over in that case. A capture listener on document runs
     before the form's own listener, and stopPropagation() keeps the theme
     handler from running at all, so there is no double add. */
  const initDrawerlessAdd = () => {
    on(document, 'submit', (e) => {
      const form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      if (!form.closest('product-form')) return;
      if (document.querySelector('cart-container')) return;  /* theme owns it */

      e.preventDefault();
      e.stopPropagation();

      const btn = form.querySelector('.btn-atc') || form.querySelector('[type="submit"]');
      const restore = () => {
        if (!btn) return;
        btn.classList.remove('loading');
        btn.disabled = false;
        btn.setAttribute('aria-busy', 'false');
      };
      if (btn) {
        btn.classList.add('loading');
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) {
            throw new Error((data && (data.description || data.message)) || 'Add to cart failed');
          }
          window.dispatchEvent(new CustomEvent('kt.cart.add', { detail: data }));
          syncCartBadge();
          restore();
          openCartAfterAdd();
        })
        .catch((err) => {
          restore();
          showAtcError(form, err && err.message ? err.message : 'Add to cart failed');
        });
    }, true);
  };

  /* With a third-party cart app the theme drawer is not rendered, so cart
     controls must hand over to the app instead of toggling nothing. */
  const initCartHandover = () => {
    on(document, 'click', (e) => {
      const trigger = e.target.closest('[data-or-cart-open]');
      if (!trigger) return;
      if (trigger.dataset.orCartOpen !== 'app') return;   /* 'page' just follows the href */

      const selector = trigger.dataset.orCartSelector;
      const evName = trigger.dataset.orCartEvent;

      /* Prefer the app's documented event, then its own launcher button. If
         neither is configured or present, fall through to /cart so the shopper
         is never stranded. */
      if (evName) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent(evName, { bubbles: true }));
        return;
      }

      if (selector) {
        const launcher = document.querySelector(selector);
        if (launcher) {
          e.preventDefault();
          launcher.click();
        }
      }
    });
  };

  /* The badge has to track the cart whoever changed it — theme, app, or a
     bare fetch from anywhere. Watch the cart endpoints themselves rather than
     trusting any one library's events. */
  /* The app repaints its drawer after its own fetches, which would put the
     stale count back — watch its shadow root and re-sync. */
  const watchAppCartHeader = () => {
    const host = document.querySelector('[data-app="shiprocket-smart-cart"]');
    const sr = host && host.shadowRoot;
    if (!sr) return;
    let pending = false;
    new MutationObserver(() => {
      if (pending) return;
      pending = true;
      setTimeout(() => { pending = false; syncCartBadge(); }, 250);
    }).observe(sr, { childList: true, subtree: true, characterData: true });
  };

  const initCartBadgeWatch = () => {
    /* The app mounts late; retry briefly rather than assuming it is there. */
    let tries = 0;
    const t = setInterval(() => {
      tries += 1;
      const host = document.querySelector('[data-app="shiprocket-smart-cart"]');
      if ((host && host.shadowRoot) || tries > 20) {
        clearInterval(t);
        watchAppCartHeader();
        syncCartBadge();
      }
    }, 500);

    const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i;
    let queued = false;
    const refresh = () => {
      if (queued) return;
      queued = true;
      setTimeout(() => { queued = false; syncCartBadge(); }, 350);
    };

    if (window.fetch && !window.fetch.__orWrapped) {
      const nativeFetch = window.fetch.bind(window);
      const wrapped = (input, init) => {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        return nativeFetch(input, init).then((res) => {
          if (CART_WRITE.test(url)) refresh();
          return res;
        });
      };
      wrapped.__orWrapped = true;
      window.fetch = wrapped;
    }

    const XHR = window.XMLHttpRequest;
    if (XHR && !XHR.prototype.__orWrapped) {
      const open = XHR.prototype.open;
      XHR.prototype.open = function (method, url) {
        this.__orCartUrl = url;
        return open.apply(this, arguments);
      };
      const send = XHR.prototype.send;
      XHR.prototype.send = function () {
        this.addEventListener('loadend', () => {
          if (CART_WRITE.test(this.__orCartUrl || '')) refresh();
        });
        return send.apply(this, arguments);
      };
      XHR.prototype.__orWrapped = true;
    }

    /* Coming back from checkout, the app cart, or a bfcache restore. */
    window.addEventListener('pageshow', (e) => { if (e.persisted) syncCartBadge(); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') syncCartBadge();
    });

    syncCartBadge();
  };

  /* ------------------------------------------------ shoppable video state */

  /* The section paints a play badge over each card and hides it via
     `data-playing="true"`. Autoplaying videos never got that flag, so the badge
     sat on top of a playing video. Drive the flag off the real media events. */
  const initShoppableVideos = () => {
    document.querySelectorAll('.shoppable-videos video').forEach((video) => {
      const card = video.closest('.card');
      if (!card) return;

      const mark = () => {
        const playing = !video.paused && !video.ended && video.readyState > 2;
        card.dataset.playing = playing ? 'true' : 'false';
      };

      ['play', 'playing', 'timeupdate', 'pause', 'ended', 'emptied'].forEach((ev) =>
        video.addEventListener(ev, mark));
      mark();
    });
  };

  document.addEventListener('shopify:section:load', initShoppableVideos);
  document.addEventListener('shopify:section:load', () => relocateDrawers());

  /* ------------------------------------------ long collection descriptions */

  /* Some collections carry a few paragraphs of copy over a short banner image.
     The header clips overflow, so that copy just disappeared mid-sentence.
     Clamp it instead and move the full text into a sheet behind "Read more". */

  const READ_MORE_SHEET_ID = 'or-readmore-sheet';

  const readMoreSheet = () => {
    let sheet = document.getElementById(READ_MORE_SHEET_ID);
    if (sheet) return sheet;

    sheet = document.createElement('dialog');
    sheet.id = READ_MORE_SHEET_ID;
    sheet.className = 'or-sheet';
    sheet.innerHTML =
      '<div class="or-sheet__inner">' +
        '<div class="or-sheet__head">' +
          '<h2 class="or-sheet__title"></h2>' +
          '<button class="or-sheet__close" type="button" aria-label="Close">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" ' +
            'stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true">' +
            '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="or-sheet__body rte"></div>' +
      '</div>';

    document.body.appendChild(sheet);

    const close = () => sheet.close();
    sheet.querySelector('.or-sheet__close').addEventListener('click', close);

    /* Clicking the backdrop means clicking the dialog itself — the inner panel
       covers everything else. */
    sheet.addEventListener('click', (e) => { if (e.target === sheet) close(); });

    sheet.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      const opener = sheet._opener;
      sheet._opener = null;
      if (opener && document.contains(opener)) opener.focus();
    });

    return sheet;
  };

  const openReadMore = (title, html, opener) => {
    const sheet = readMoreSheet();
    const heading = sheet.querySelector('.or-sheet__title');
    heading.textContent = title || '';
    heading.hidden = !title;
    sheet.querySelector('.or-sheet__body').innerHTML = html;
    sheet._opener = opener || null;
    document.documentElement.style.overflow = 'hidden';
    sheet.showModal();
    sheet.querySelector('.or-sheet__body').scrollTop = 0;
  };

  /* Any control can open the sheet: point `data-or-sheet` at a <template> or
     container id and give it a `data-or-sheet-title`. Used by the size chart. */
  const initSheetTriggers = () => {
    on(document, 'click', (e) => {
      const trigger = e.target.closest('[data-or-sheet]');
      if (!trigger) return;

      const source = document.getElementById(trigger.dataset.orSheet);
      if (!source) return;

      e.preventDefault();
      const html = source.content ? source.innerHTML : source.innerHTML;
      openReadMore(trigger.dataset.orSheetTitle || '', html, trigger);
    });
  };

  const initReadMore = () => {
    document.querySelectorAll('.collection-header .description').forEach((desc) => {
      if (desc.dataset.orReadmore) return;
      desc.dataset.orReadmore = 'true';

      const full = desc.innerHTML;
      const title = document.querySelector('.collection-header .title')?.textContent.trim() || '';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'or-readmore';
      btn.textContent = 'Read more';
      btn.hidden = true;
      btn.addEventListener('click', () => openReadMore(title, full, btn));
      desc.insertAdjacentElement('afterend', btn);

      /* Only clamp when the copy genuinely doesn't fit — measure the unclamped
         height first, otherwise short descriptions get a needless "Read more"
         and a fade over text that was never truncated. Hidden breakpoint twins
         measure 0, so re-check whenever the layout changes. */
      let measuring = false;
      let lastWidth = -1;

      const measure = () => {
        if (measuring || !desc.offsetParent) return;
        measuring = true;
        desc.classList.remove('or-clamp');
        const full = desc.scrollHeight;
        desc.classList.add('or-clamp');
        const overflows = full > desc.clientHeight + 4;
        desc.classList.toggle('or-clamp', overflows);
        btn.hidden = !overflows;
        lastWidth = desc.clientWidth;
        /* Toggling the clamp resizes `desc`, which would re-enter through the
           observer below — release the latch only after that reflow settles. */
        requestAnimationFrame(() => { measuring = false; });
      };

      measure();

      /* Observe the parent, not `desc`: only a real layout change should
         re-measure, and the parent's width is what drives the wrap. */
      if ('ResizeObserver' in window && desc.parentElement) {
        new ResizeObserver(() => {
          if (desc.offsetParent && desc.clientWidth !== lastWidth) measure();
        }).observe(desc.parentElement);
      }

      window.addEventListener('resize', measure);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    });
  };

  /* ------------------------------------------ live search on /search page */

  /* Renders the page's own results section as you type, via the Section
     Rendering API — every result, with the page's filters and pagination.

     All four searchable types are fetched in one parallel round, so the tab
     counts and the visible results come from the same request set: a tab can
     never claim a count the panel then contradicts, and switching tabs costs
     nothing because the markup is already in hand. */
  function initSearchPageLive() {
    const host = document.querySelector('[data-or-search-live]');
    if (!host) return;

    const input = host.querySelector('input[type="search"]');
    const form = host.querySelector('form');
    const clearBtn = host.querySelector('[data-or-search-clear]');
    const tabsEl = document.querySelector('[data-or-search-tabs]');
    const sectionId = host.dataset.sectionId;
    if (!input || !sectionId) return;

    /* Shopify's storefront search accepts product, page and article only.
       `type=collection` is not rejected — it silently returns products, which
       is why the Collections tab showed product cards under a "journal
       entries" heading and a count that belonged to neither. Collections are
       matched client-side from /collections.json instead. */
    const TYPES = [
      { id: 'product', label: 'Products' },
      { id: 'page', label: 'Pages' },
      { id: 'article', label: 'Journal' },
    ];
    const COLLECTION_TAB = { id: 'collection', label: 'Collections' };
    const TABS = [TYPES[0], COLLECTION_TAB, TYPES[1], TYPES[2]];

    const resultsOf = (scope) => scope.querySelector('[data-or-search-results]');

    let controller = null;
    let timer = null;
    let seq = 0;
    let renderedTerm = input.value.trim();
    let active = new URLSearchParams(window.location.search).get('type') || 'product';
    let cache = {};          /* type -> results element for the current term */

    const syncClearBtn = () => { if (clearBtn) clearBtn.hidden = !input.value; };

    /* Shopify's `options[prefix]=last` does no prefix matching on this store —
       "strapl" returns 0 while "strapl*" returns 177 — so wildcard the last
       word. Deleting a character can then only widen the match. */
    const asPrefixQuery = (t) => {
      if (!t) return t;
      if (t.indexOf('"') !== -1 || t.indexOf('*') !== -1) return t;
      const parts = t.split(/\s+/);
      parts[parts.length - 1] += '*';
      return parts.join(' ');
    };

    const urlFor = (term, type) => {
      const url = new URL(window.location.origin + window.location.pathname);
      url.searchParams.set('q', asPrefixQuery(term));
      url.searchParams.set('type', type);
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('section_id', sectionId);
      return url;
    };

    const show = (type) => {
      const el = cache[type];
      const current = resultsOf(document);
      if (el && current) current.replaceWith(el);
      else if (el) document.querySelector('.search-main').appendChild(el);
      else if (current) current.remove();
      active = type;
      paintTabs();
    };

    const paintTabs = () => {
      if (!tabsEl) return;
      const available = TABS.filter((t) => (cache[t.id]?.dataset.count | 0) > 0);

      /* One type with results is not a choice — no tab bar. */
      if (available.length < 2) { tabsEl.hidden = true; tabsEl.innerHTML = ''; return; }

      tabsEl.hidden = false;
      tabsEl.innerHTML = available.map((t) => {
        const n = cache[t.id].dataset.count;
        const on = t.id === active ? ' is-active' : '';
        return '<button type="button" class="or-stab' + on + '" data-or-tab="' + t.id + '">' +
               '<span>' + t.label + '</span><span class="or-stab__n">' + n + '</span></button>';
      }).join('');
    };

    /* One fetch per page load; the list is small and rarely changes. */
    let collectionsPromise = null;
    const allCollections = () => {
      if (!collectionsPromise) {
        collectionsPromise = fetch('/collections.json?limit=250')
          .then((r) => (r.ok ? r.json() : { collections: [] }))
          .then((d) => d.collections || [])
          .catch(() => []);
      }
      return collectionsPromise;
    };

    const matchCollections = async (term) => {
      const q = term.trim().toLowerCase();
      if (!q) return [];
      const words = q.split(/\s+/).filter(Boolean);
      const list = await allCollections();
      return list.filter((c) => {
        const hay = (c.title + ' ' + c.handle).toLowerCase();
        /* Every word must appear as a prefix somewhere, mirroring the
           wildcarded product query so the tabs agree on what "matching" means. */
        return words.every((w) => hay.split(/[^a-z0-9]+/).some((tok) => tok.startsWith(w)));
      });
    };

    /* A collection with no image of its own borrows its first product's photo.
       Fetched lazily and cached per handle, then patched into the card — so a
       keystroke is never blocked waiting on it. */
    const firstProductImg = {};
    const fillMissingArt = (scope) => {
      scope.querySelectorAll('[data-or-collection-art]').forEach((media) => {
        const handle = media.dataset.orCollectionArt;

        const paint = (src) => {
          if (!src || !media.isConnected) return;
          const img = document.createElement('img');
          img.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=300';
          img.alt = '';
          img.width = 150; img.height = 150; img.loading = 'lazy';
          media.appendChild(img);
        };

        if (handle in firstProductImg) { paint(firstProductImg[handle]); return; }

        fetch('/collections/' + handle + '/products.json?limit=1')
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => {
            const prod = d && d.products && d.products[0];
            const src = prod && prod.images && prod.images[0] && prod.images[0].src;
            firstProductImg[handle] = src || '';
            paint(src);
          })
          .catch(() => { firstProductImg[handle] = ''; });
      });
    };

    const buildCollectionResults = (items) => {
      const wrap = document.createElement('div');
      wrap.setAttribute('data-or-search-results', '');
      wrap.setAttribute('aria-live', 'polite');
      wrap.className = 'search-inner pt-0 pb-10';
      wrap.dataset.count = items.length;

      const label = items.length === 1 ? '1 collection' : items.length + ' collections';
      const rows = items.map((c) => {
        const src = c.image && c.image.src;
        const img = src
          ? '<img src="' + src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=300" alt="" width="150" height="150" loading="lazy">'
          : '';
        /* No image on the collection — mark it so the first product's photo can
           be filled in once fetched. */
        const artHook = src ? '' : ' data-or-collection-art="' + c.handle + '"';
        const n = c.products_count;
        const meta = n ? '<span class="or-sresult__meta">' + n + (n === 1 ? ' product' : ' products') + '</span>' : '';
        return '<li class="or-sresult" role="listitem"><a href="/collections/' + c.handle + '">' +
          '<span class="or-sresult__media"' + artHook + '>' + img + '</span>' +
          '<span class="or-sresult__copy">' +
            '<span class="or-sresult__kicker">Collection</span>' +
            '<span class="or-sresult__title">' + c.title + '</span>' + meta +
          '</span></a></li>';
      }).join('');

      wrap.innerHTML =
        '<div class="container">' +
          '<div class="search-utilities mb-4"><p class="search-product-count small mb-0">' + label + '</p></div>' +
          '<ul class="or-sresults or-sresults--collections list-unstyled mb-0" role="list">' + rows + '</ul>' +
        '</div>';
      fillMissingArt(wrap);
      return wrap;
    };

    const render = async (term) => {
      const mine = ++seq;

      if (controller) controller.abort();
      controller = new AbortController();

      host.classList.add('is-loading');
      try {
        const [responses, collections] = await Promise.all([
          Promise.all(
            TYPES.map((t) =>
              fetch(urlFor(term, t.id), { signal: controller.signal })
                .then((r) => (r.ok ? r.text() : ''))
                .catch(() => ''))
          ),
          matchCollections(term),
        ]);

        /* Abort does not always beat a response already in flight, so check the
           token before touching the DOM. */
        if (mine !== seq) return;

        const next = {};
        responses.forEach((html, i) => {
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const el = resultsOf(doc);
          if (el) next[TYPES[i].id] = el;
        });
        if (collections.length) next.collection = buildCollectionResults(collections);
        cache = next;

        /* Fall back to the first type that actually has something. */
        if (!((cache[active]?.dataset.count | 0) > 0)) {
          const first = TABS.find((t) => (cache[t.id]?.dataset.count | 0) > 0);
          active = first ? first.id : 'product';
        }

        show(active);
        renderedTerm = term;

        const shown = new URL(window.location);
        if (term) shown.searchParams.set('q', term); else shown.searchParams.delete('q');
        if (term) shown.searchParams.set('type', active); else shown.searchParams.delete('type');
        window.history.replaceState({}, '', shown);
      } catch (e) {
        /* AbortError means a newer keystroke won; leave the results alone. */
      } finally {
        if (mine === seq) host.classList.remove('is-loading');
      }
    };

    const queue = () => {
      const term = input.value.trim();
      syncClearBtn();
      /* Compare against what is on screen, not the last term typed, or
         backspacing to a term you passed through leaves stale results up. */
      if (term === renderedTerm && !host.classList.contains('is-loading')) {
        clearTimeout(timer);
        return;
      }
      clearTimeout(timer);
      timer = setTimeout(() => render(term), 280);
    };

    on(input, 'input', queue);
    on(input, 'search', queue);

    on(tabsEl, 'click', (e) => {
      const btn = e.target.closest('[data-or-tab]');
      if (!btn) return;
      show(btn.dataset.orTab);
      const shown = new URL(window.location);
      shown.searchParams.set('type', active);
      window.history.replaceState({}, '', shown);
    });

    on(clearBtn, 'click', () => {
      input.value = '';
      syncClearBtn();
      clearTimeout(timer);
      render('');
      input.focus();
    });

    on(form, 'submit', (e) => {
      e.preventDefault();
      clearTimeout(timer);
      render(input.value.trim());
    });

    syncClearBtn();
    /* Landing on /search?q=… already has products rendered; fetch the rest so
       the tabs appear without the shopper typing. */
    if (renderedTerm) render(renderedTerm);
  }

  /* ------------------------------------------------------- drawer stacking */

  /* theme.liquid wraps the header group in Bootstrap's `.sticky-top`, which is
     `position: sticky; z-index: 1020`. That is a stacking context, and the menu
     / search / cart drawers are rendered inside the header section — so their
     own `z-index: 1045` was capped at the group's 1020 and anything above that
     painted over them: the collection toolbar (1020, later in the DOM), the
     mobile bottom nav (1035) and the chat bubble (1045).

     Moving the drawers to <body> puts them back in the root stacking context,
     where Bootstrap's scale works as intended. Triggers find them by id, so
     relocating costs nothing. */
  const relocateDrawers = () => {
    document.querySelectorAll('#header-group .offcanvas').forEach((el) => {
      /* Never move an open drawer — that would tear down its transition and
         Bootstrap's scroll lock mid-flight. */
      if (el.classList.contains('show') || el.classList.contains('showing')) return;
      document.body.appendChild(el);
    });
  };

  /* ---------------------------------------------- product card colour swatches */

  /* Tapping a card swatch should preview that colour in place, not navigate.
     The href stays a real product link so the control still works without JS
     and remains crawlable — we just take over the click. */
  const initCardSwatches = () => {
    on(document, 'click', (e) => {
      const swatch = e.target.closest('[data-or-swatch]');
      if (!swatch) return;

      const src = swatch.dataset.swatchImg;
      const card = swatch.closest('product-card, .product-card');
      if (!src || !card) return;

      const media = card.querySelector('.or-card-media');
      const img =
        (media && media.querySelector('img:not(.or-card-media__hover)')) ||
        card.querySelector('.carousel-item.active img') ||
        card.querySelector('.product-card-img-wrapper img');

      if (!img) return;

      e.preventDefault();

      img.src = src;
      img.removeAttribute('srcset');

      /* The hover cross-fade still holds the original product's second photo,
         which would contradict the colour just chosen. */
      if (media) media.classList.remove('has-hover');

      card.querySelectorAll('[data-or-swatch]').forEach((s) =>
        s.classList.toggle('is-active', s === swatch));
    });
  };

  /* --------------------------------------------------------- newsletter */

  /* Submitted natively, on purpose. Shopify's bot protection answers 403
     "Verifying your connection" to programmatic POSTs at /contact, so a fetch
     is unreliable — a real form navigation is the only path that always works.
     The reload is therefore accepted, and the job here is to make it land well:
     come back to the page the shopper was on, and confirm unmistakably. */
  const initNewsletter = () => {
    /* 1. Return to this page, not wherever Shopify decides. */
    document.addEventListener('submit', (e) => {
      const form = e.target.closest && e.target.closest('form.or-ftr__form, form[data-or-newsletter]');
      if (!form) return;

      const phone = form.querySelector('input[type="tel"]');
      if (phone && phone.value.trim()) {
        const raw = phone.value.trim();
        const digits = raw.replace(/[^\d]/g, '');
        if (raw[0] !== '+' && digits.length >= 6 && digits.length <= 12) {
          phone.value = (phone.dataset.orCountry || '+91').replace(/[^\d+]/g, '') + digits;
        }
      }

      if (!form.querySelector('input[name="return_to"]')) {
        const back = document.createElement('input');
        back.type = 'hidden';
        back.name = 'return_to';
        back.value = window.location.pathname + '?customer_posted=true#or-newsletter';
        form.appendChild(back);
      }
    }, true);

    /* 2. On the way back, say so in a way nobody can miss. */
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') !== 'true') return;

    const form = document.querySelector('form.or-ftr__form, form[data-or-newsletter]');
    const message =
      form?.dataset.orNewsletterSuccess ||
      form?.querySelector('[data-or-newsletter-ok]')?.textContent.trim() ||
      "You're on the list. Welcome to Oraani.";

    openReadMore(
      form?.dataset.orNewsletterSuccessTitle || 'Thank you',
      '<p class="or-sheet__lead">' + message + '</p>',
      null
    );

    /* Strip the flag so a refresh does not re-announce it. */
    params.delete('customer_posted');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
  };

  /* ------------------------------------------------- concierge clearance */

  /* The bubble has to clear whatever is pinned to the bottom — the mobile nav,
     and the sticky add-to-cart bar once it slides in. That bar is content-sized
     (a two-line product title makes it much taller than a one-line one), so a
     fixed CSS guess was always going to be wrong for some products. Measure it. */
  const initChatOffset = () => {
    const chat = document.querySelector('.or-chat');
    if (!chat) return;

    const bar = document.getElementById('sticky-atc');
    const nav = document.querySelector('.or-mnav');
    const GAP = 12;

    const apply = () => {
      let base = 0;

      if (nav && getComputedStyle(nav).display !== 'none' && !nav.classList.contains('is-tucked')) {
        base += nav.offsetHeight;
      }

      /* The bar keeps its layout box while hidden (visibility, not display), so
         it measures correctly even before it is first shown. */
      /* `visibility: hidden` keeps the layout box, so this measures correctly
         even before the bar is first shown. */
      if (bar && bar.classList.contains('show')) base += bar.offsetHeight;

      document.body.style.setProperty('--or-chat-offset', base + GAP + 'px');
    };

    apply();

    if (bar) {
      new MutationObserver(apply).observe(bar, { attributes: true, attributeFilter: ['class'] });
      if ('ResizeObserver' in window) new ResizeObserver(apply).observe(bar);
    }
    if (nav) new MutationObserver(apply).observe(nav, { attributes: true, attributeFilter: ['class'] });

    window.addEventListener('resize', apply);
  };

  /* ---------------------------------------------------------------- video */

  /* Poster-first: the MP4 is preload="none" until someone asks for it, so a
     story page does not ship megabytes to every visitor. */
  const initVideo = () => {
    on(document, 'click', (e) => {
      const btn = e.target.closest('[data-or-video-play]');
      if (!btn) return;
      const frame = btn.closest('.or-vid__frame');
      const video = frame && frame.querySelector('video');
      if (!video) return;
      e.preventDefault();
      frame.classList.add('is-playing');
      video.setAttribute('controls', 'controls');
      video.play().catch(() => { /* autoplay policy — controls are visible anyway */ });
    });
  };

  /* ------------------------------------------------------------------ boot */

  const define = (name, ctor) => { if (!customElements.get(name)) customElements.define(name, ctor); };

  define('oraani-header', OraaniHeader);
  define('oraani-hero', OraaniHero);
  define('oraani-mobile-nav', OraaniMobileNav);
  define('oraani-chat', OraaniChat);
  define('oraani-size-calc', OraaniSizeCalc);
  define('oraani-price', OraaniPrice);
  define('oraani-buy-now', OraaniBuyNow);

  const boot = () => {
    relocateDrawers();
    initOraaniReviews();
    initRatingBadges();
    initReviewWrite();
    initReviewsShowAll();
    initStockIndicator();
    initDrawerlessAdd();
    initCartHandover();
    initCartBadgeWatch();
    initCardSwatches();
    initSearchExtras();
    initCollectionToolbar();
    initShoppableVideos();
    initReadMore();
    initSheetTriggers();
    initVideo();
    initNewsletter();
    initChatOffset();
    initSearchPageLive();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
